<h1 mat-dialog-title>Snack rewards for {{data.companyName}}</h1>
<div mat-dialog-content class="snack-rewards-dialog-content">
  <div class="setting-mode-panel">
    <div class="setting-mode-buttons">
      <button mat-stroked-button type="button" [class.active-mode]="mode === 'inherit'" (click)="setMode('inherit')">
        {{canInherit() ? 'Use parent setting' : 'Use brand defaults'}}
      </button>
      <button mat-stroked-button type="button" [class.active-mode]="mode === 'none'" (click)="setMode('none')">
        No snacks
      </button>
      <button mat-stroked-button type="button" [class.active-mode]="mode === 'custom'" (click)="setMode('custom')">
        Custom snacks
      </button>
    </div>
    @if (mode === 'inherit' && canInherit()) {
      <p class="setting-mode-help">
        This company will use snack reward selections from {{data.parentCompanyName || 'its parent company'}}.
      </p>
    }
    @if (mode === 'inherit' && !canInherit()) {
      <p class="setting-mode-help">
        This company will use the default brand and campaign snack rewards.
      </p>
    }
    @if (mode === 'none') {
      <p class="setting-mode-help">
        This company explicitly overrides the parent setting and shows no snacks.
      </p>
    }
    @if (mode === 'custom') {
      <p class="setting-mode-help">
        Selected snacks are available to this company even when their brand or company campaign targeting differs.
      </p>
    }
  </div>

  @if (hasImportOptions()) {
    <div class="snack-import-helper" [class.collapsed]="!isImportHelperExpanded">
      <button
        type="button"
        class="snack-import-helper-header"
        [attr.aria-expanded]="isImportHelperExpanded"
        (click)="toggleImportHelper()">
        <span class="snack-import-helper-title">Select snack rewards from existing brand or company</span>
        <span class="snack-import-helper-action">{{isImportHelperExpanded ? 'Hide' : 'Show'}}</span>
      </button>
      @if (isImportHelperExpanded) {
        <div class="snack-import-helper-body">
          <div class="snack-import-controls">
            <mat-form-field appearance="outline">
              <mat-label>Source type</mat-label>
              <mat-select
                [(ngModel)]="importSourceType"
                [disabled]="isImportLoading"
                (ngModelChange)="onImportSourceTypeChange($event)">
                @if (data.brandOptions.length > 0) {
                  <mat-option value="brand">Existing brand</mat-option>
                }
                @if (data.companyOptions.length > 0) {
                  <mat-option value="company">Existing company</mat-option>
                }
              </mat-select>
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>{{importSourceType === 'company' ? 'Company snack source' : 'Brand snack source'}}</mat-label>
              @if (importSourceType === 'brand') {
                <mat-select [(ngModel)]="selectedBrandId" [disabled]="isImportLoading">
                  @for (brand of data.brandOptions; track brand) {
                    <mat-option [value]="brand.id">
                      {{brand.label}} @if (brand.subtitle) {
                      <span>({{brand.subtitle}})</span>
                    }
                  </mat-option>
                }
              </mat-select>
            }
            @if (importSourceType === 'company') {
              <mat-select [(ngModel)]="selectedCompanyId" [disabled]="isImportLoading">
                @for (company of data.companyOptions; track company) {
                  <mat-option [value]="company.id">
                    {{company.label}} @if (company.subtitle) {
                    <span>({{company.subtitle}})</span>
                  }
                </mat-option>
              }
            </mat-select>
          }
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Apply behavior</mat-label>
          <mat-select
            [(ngModel)]="importApplyMode"
            [disabled]="isImportLoading"
            (ngModelChange)="setImportApplyMode($event)">
            <mat-option value="replace">Replace current selection</mat-option>
            <mat-option value="merge">Merge with current selection</mat-option>
          </mat-select>
        </mat-form-field>
        <button
          mat-stroked-button
          type="button"
          color="primary"
          [disabled]="(importSourceType === 'brand' ? !selectedBrandId : !selectedCompanyId) || isImportLoading"
          (click)="loadImportPreview()">
          Preview Snacks
        </button>
        @if (isImportLoading) {
          <mat-spinner [diameter]="24"></mat-spinner>
        }
      </div>
      @if (importError) {
        <app-inlinemessage level="warning" [message]="importError"></app-inlinemessage>
      }
      @if (importPreviewLoaded && !importError) {
        <div class="snack-import-preview">
          @if (importPreviewUnavailableRewardIds.length > 0) {
            <app-inlinemessage
              level="warning"
              message="Some saved snack IDs are no longer globally selectable and will be skipped.">
            </app-inlinemessage>
          }
          <p class="snack-import-summary">
            {{importPreviewRewardIds.length}} selectable snacks from {{importSourceLabel}}.
            {{importPreviewAddedRewardIds.length}} would be added, {{importPreviewOverlappingRewardIds.length}} are already selected, and {{importPreviewRemovedRewardIds.length}} would be removed.
          </p>
          <div class="snack-import-diff">
            <div>
              <h3>Would Add</h3>
              @if (importPreviewAddedRewardIds.length === 0) {
                <p>No snacks</p>
              }
              @if (importPreviewAddedRewardIds.length > 0) {
                <ul>
                  @for (rewardId of importPreviewAddedRewardIds; track trackRewardId($index, rewardId)) {
                    <li>{{getRewardLabel(rewardId)}}</li>
                  }
                </ul>
              }
            </div>
            <div>
              <h3>Already Selected</h3>
              @if (importPreviewOverlappingRewardIds.length === 0) {
                <p>No snacks</p>
              }
              @if (importPreviewOverlappingRewardIds.length > 0) {
                <ul>
                  @for (rewardId of importPreviewOverlappingRewardIds; track trackRewardId($index, rewardId)) {
                    <li>{{getRewardLabel(rewardId)}}</li>
                  }
                </ul>
              }
            </div>
            <div>
              <h3>Would Remove</h3>
              @if (importPreviewRemovedRewardIds.length === 0) {
                <p>No snacks</p>
              }
              @if (importPreviewRemovedRewardIds.length > 0) {
                <ul>
                  @for (rewardId of importPreviewRemovedRewardIds; track trackRewardId($index, rewardId)) {
                    <li>{{getRewardLabel(rewardId)}}</li>
                  }
                </ul>
              }
            </div>
            @if (importPreviewUnavailableRewardIds.length > 0) {
              <div>
                <h3>Skipped</h3>
                <ul>
                  @for (rewardId of importPreviewUnavailableRewardIds; track trackRewardId($index, rewardId)) {
                    <li>
                      {{rewardId}} - Not found in the globally selectable snack catalog
                    </li>
                  }
                </ul>
              </div>
            }
          </div>
          <button mat-raised-button type="button" color="accent" [disabled]="!canApplyImportPreview()" (click)="applyImportPreview()">
            Apply To Custom Selection
          </button>
          @if (importPreviewApplied) {
            <p class="snack-import-applied">
              Snack rewards were applied to the dialog selection. Use Done to save.
            </p>
          }
        </div>
      }
    </div>
  }
</div>
}

@if (rewards.length === 0) {
  <app-inlinemessage level="warning" message="No globally selectable snack rewards are available."></app-inlinemessage>
}
@if (rewards.length > 0) {
  <div class="snack-reward-toolbar">
    <div class="snack-reward-selection-controls">
      <mat-checkbox
        aria-label="Select or clear all globally selectable snack rewards"
        [checked]="isAllSelected()"
        [indeterminate]="isSomeSelected() && !isAllSelected()"
        [disabled]="!isCustomMode()"
        (change)="toggleAllRewards($event.checked)">
        Select all
      </mat-checkbox>
      <span>{{getSelectedCount()}} of {{rewards.length}} selected</span>
    </div>
    <div class="snack-reward-selection-controls">
      <mat-checkbox [checked]="showSelectedOnly" (change)="setShowSelectedOnly($event.checked)">
        Show selected only
      </mat-checkbox>
      <button mat-button type="button" [disabled]="!isCustomMode() || getSelectedCount() === 0" (click)="clearSelected()">
        Clear
      </button>
    </div>
  </div>
}
@if (rewards.length > 0 && showSelectedOnly && displayedRewards.length === 0) {
  <app-inlinemessage
    level="warning"
    message="No selected snack rewards to show">
  </app-inlinemessage>
}
@if (displayedRewards.length > 0) {
  <div class="snack-reward-list" [class.readonly-list]="!isCustomMode()">
    @for (reward of displayedRewards; track trackReward($index, reward)) {
      <div class="snack-reward-row" [class.selected-row]="isSelected(reward.id)">
        <mat-checkbox
          class="snack-reward-checkbox"
          (change)="toggleReward(reward.id, $event.checked)"
          [checked]="isSelected(reward.id)"
          [disabled]="!isCustomMode()">
        </mat-checkbox>
        <div class="reward-text">
          <div class="reward-title">{{getRewardTitle(reward)}}</div>
          <div class="reward-subtitle">{{getRewardSubtitle(reward)}}</div>
        </div>
      </div>
    }
  </div>
}
</div>
<div class="buttons snack-rewards-actions" mat-dialog-actions>
  <button mat-raised-button class="donebutton" color="primary" (click)="okClicked()">Done</button>
  <button mat-button (click)="onDismiss()">Cancel</button>
</div>
